import React, { FC, memo } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType } from 'src/types/table';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Select from 'src/components/Select';
import { SkladonMappingType } from '../../types/skladonMapping';
import translate from '@translate';
import { Box } from '@mui/material';
import { EditKeysType } from './constants';

const allColumns: TableColumnType[] = [
    {
        key: 'UID',
        label: 'uid',
        customRender: true,
        numericSort: true
    },
    {
        key: 'NAME',
        label: 'name'
    },
    {
        key: 'VALUE',
        label: 'value',
        customRender: true
    },
    {
        key: 'IMPORT',
        label: 'import',
        type: 'select',
        selectOption: 'boolNumberCodes',
        customRender: true
    },
    {
        key: 'EXPORT',
        label: 'export',
        type: 'select',
        selectOption: 'boolNumberCodes',
        customRender: true
    },
    {
        key: 'SAVED',
        label: 'saved',
        customRender: true
    }
];

export interface StatusesTablePropsType {
    data?: SkladonMappingType[] | null;
    boolNumberCodes: ConstantsCodeType[];
    orderStatuses: ConstantsCodeType[];
    changeValue: (key: EditKeysType, value: any, error: boolean, id: number) => void;
    changeValues: (changes: { key: EditKeysType, value: any, error: boolean }[], id: number) => void;
    loading: boolean;
}

const IMPORT_STATUSES = ['preReceived', 'canceled', 'incorrect', 'suspended', 'preOrder', 'waitingForShipment', 'productsNotAvailable', 'waitingForPayment'];
const StatusesTable: FC<StatusesTablePropsType> = (props) => {
    const getInputName = (id: number, key: string) => `product-${id}-${key}`;

    const customRender: CustomRenderType = (column, row) => {
        const name = getInputName(row.ID, column.key);
        switch (column.key) {
            case 'VALUE':
                return (
                    <Select
                        options={props.orderStatuses}
                        value={row.VALUE}
                        onChange={(value, error) => {
                            const importValue = IMPORT_STATUSES.includes(String(value)) ? '1' : '0';
                            const exportValue = value === 'preReceived' ? '0' : '1';
                            props.changeValues([
                                { key: 'VALUE', value, error },
                                { key: 'IMPORT', value: importValue, error: false },
                                { key: 'EXPORT', value: exportValue, error: false }
                            ], row.ID);
                        }}
                        name={name}
                        fullWidth
                    />
                );
            case 'IMPORT':
                return (
                    <Select
                        options={props.boolNumberCodes}
                        value={row.IMPORT}
                        onChange={(value, error) => props.changeValue('IMPORT', value, error, row.ID)}
                        name={name}
                        fullWidth
                    />
                );
            case 'EXPORT':
                return (
                    <Select
                        options={props.boolNumberCodes}
                        value={row.EXPORT}
                        onChange={(value, error) => props.changeValue('EXPORT', value, error, row.ID)}
                        name={name}
                        fullWidth
                    />
                );
            case 'SAVED':
                const isSaved = row.ID && row.ID > 0;
                return (
                    <Box
                        sx={{
                            color: isSaved ? 'green' : 'red',
                            fontWeight: 'bold'
                        }}
                    >
                        {isSaved
                            ? translate('integration.mappingTable.saved.yes')
                            : translate('integration.mappingTable.saved.notSaved')
                        }
                    </Box>
                );
            default:
                return null;
        }
    };

    const getRowSx = (row: any) => {
        const isSaved = row.ID && row.ID > 0;
        if (!isSaved) {
            return {
                backgroundColor: '#ffebee' // Light red background for unsaved rows
            };
        }
        return {};
    };

    return (
        <TableSimple
            rowIdKey="ID"
            rows={props.data || []}
            columsLabelPrefix="integration.statusesMappingTable"
            allColumns={allColumns}
            initSort={{ column: 'UID', direction: 'ASC', numericOrAlphabetic: true }}
            selectOptions={{
                boolNumberCodes: props.boolNumberCodes
            }}
            customRender={customRender}
            rowSxFce={getRowSx}
            disableFilter
            maxHeight={'unset'}
            defaultItems={100}
            loading={props.loading}
        />
    );
};

export default memo(StatusesTable);
